import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { styled, CSSObject } from '../theming';
import { responsiveProp } from '../utils/responsiveProp';
import { focusStyles } from '../utils';
import { BaseButton } from './BaseButton';
import type { ButtonColor, ButtonState, ButtonProps, ButtonSize } from './types';
import { getColorTokens, getSizeTokens } from './utils';

// In order to provide exact same sizes as Figma components
// we need to hardcode the height of buttons, to ovoid 2px difference coused by borders
export const buttonHeight = {
  small: '36px',
  default: '44px',
  large: '48px',
  xlarge: '64px',
  xxlarge: '80px',
};

const getColorStyles = (color: ButtonColor, theme: BabbelTheme, state?: ButtonState): CSSObject => {
  const colorSemanticTokens = getColorTokens(color, theme);
  const buttonState = state ?? 'default';

  return {
    backgroundColor: colorSemanticTokens[buttonState].background,
    color: colorSemanticTokens[buttonState].foreground,
    border: `1px solid ${colorSemanticTokens[buttonState].border}`,
  };
};

const getSizeStyles = (
  size: ButtonSize,
  hasIconOnly: boolean,
  alignIcon?: 'start' | 'end',
  loading?: boolean
): CSSObject => {
  const sizeTokens = getSizeTokens(size, hasIconOnly, loading);

  if (hasIconOnly) {
    return {
      padding: sizeTokens.padding,
    };
  }

  if (loading) {
    return {
      padding: `${sizeTokens.paddingVertical} ${sizeTokens.paddingHorizontal}`,
    };
  }

  const iconGapStyles =
    alignIcon === 'end' ? { marginLeft: sizeTokens.gap } : { marginRight: sizeTokens.gap };

  return {
    padding: `${sizeTokens.paddingVertical} ${sizeTokens.paddingHorizontal}`,
    // we display spacing only for button with icon and text
    // to avoid additional nodes in the component, we handle it in CSS
    // TODO: use CSS property `gap` instead of setting a margin for the svg when https://github.com/lessonnine/design-system.lib/issues/298 is done
    svg: iconGapStyles,
  };
};

const sharedButtonStyles = (
  {
    fullWidth,
    size,
    minWidth,
    disableHoverStyle,
    disabled,
    color,
    $loading,
    hasIconOnly,
    alignIcon,
  }: ButtonProps<'a' | 'button'> & {
    hasIconOnly: boolean;
    $loading?: boolean;
  },
  theme: BabbelTheme
): CSSObject => {
  const hoverStyle =
    disableHoverStyle || disabled
      ? {}
      : {
          ':hover': {
            ...(color && getColorStyles(color, theme, 'hover')),
          },
        };
  return {
    height: buttonHeight[size as keyof typeof buttonHeight],
    boxSizing: 'border-box',
    position: 'relative',
    borderRadius: tokens.radius.round,
    cursor: 'pointer',
    display: 'inline-flex',
    ...responsiveProp(size, (size: ButtonSize) => ({
      ...getSizeStyles(size, hasIconOnly, alignIcon, $loading),
    })),
    ...responsiveProp(fullWidth, (fullWidth) => ({
      width: fullWidth
        ? '100%'
        : hasIconOnly
        ? buttonHeight[size as keyof typeof buttonHeight]
        : 'auto',
    })),
    ...(minWidth ? { minWidth: minWidth } : {}),
    flexShrink: '0',
    justifyContent: 'center',
    alignItems: 'center',
    outline: 'none',
    textDecoration: 'none',
    margin: '0px',
    transition: 'all 100ms ease-out',
    ...(color && getColorStyles(color, theme)),
    ...hoverStyle,
    '&:enabled:active': {
      ...(color && getColorStyles(color, theme, 'active')),
    },
    ':disabled': {
      ...(color && getColorStyles(color, theme, 'disabled')),
      cursor: 'not-allowed',
    },
    ...focusStyles({ type: 'pseudo', radius: parseInt(tokens.radius.round, 10), theme }),
  };
};

export const StyledLinkButton = styled.a<
  ButtonProps<'a'> & { hasIconOnly: boolean; hasIconAndText: boolean }
>`
  ${({ theme, ...props }): CSSObject => ({
    ...sharedButtonStyles(props, theme),
  })}
`;

export const StyledButton = styled(BaseButton)<
  ButtonProps<'button'> & { hasIconOnly: boolean; hasIconAndText: boolean }
>`
  ${({ theme, ...props }): CSSObject => ({
    ...sharedButtonStyles(props, theme),
  })}
`;

export const StyledIconWrapper = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

export const SpinnerContainer = styled.div<{ spinnerSize: number }>`
  ${({ spinnerSize }): CSSObject => ({
    ...responsiveProp(spinnerSize, (spinnerSize) => ({
      height: spinnerSize,
      width: spinnerSize,
    })),
    '& + *': {
      transition: 'opacity 0.3s',
      opacity: 0,
    },
  })}
`;
